/**
 * MobX store for Portal Edge font settings: supported web font families, custom font file pending uploads,
 * and per-slot delete flags. Pairs with CustomizationEdgeStore; font files upload via POST multipart on save.
 */

import { makeAutoObservable, runInAction } from 'mobx';
import { rootContainer, TYPE } from '../../services/container/root';
import {
    EDGE_FONT_SLOTS,
    EdgeFontSlot,
    IEdgeCustomization,
    IEdgeCustomizationFontFile,
    IEdgeFontSlot,
} from '../../models/User';
import CustomizationsService from '../../services/api/customizations-service';
import { IChangeable, ISaveable } from '../types';

interface ISlotDraft {
    family?: string;
    _delete_custom_font?: boolean;
}

type SlotPatchMap = Partial<Record<EdgeFontSlot, ISlotDraft>>;

type FontSlotPutEntry = IEdgeFontSlot & { _delete_custom_font?: boolean };

export class CustomizationEdgeFontsStore implements ISaveable, IChangeable<IEdgeCustomization> {
    private _slotPatch: SlotPatchMap = {};
    private _model?: IEdgeCustomization;
    private _isSaving = false;
    private _pendingBySlot: Partial<Record<EdgeFontSlot, File>> = {};

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): CustomizationsService {
        return rootContainer.get<CustomizationsService>(TYPE.CustomizationsService);
    }

    private rowFromModel(slot: EdgeFontSlot): IEdgeFontSlot | undefined {
        return this._model?.font_slots?.find((r) => r.slot === slot);
    }

    public isSaving(): boolean {
        return this._isSaving;
    }

    /** Server snapshot before local edits; used as `__old` on PUT for audit logs. */
    public getModelForSaveAudit(): IEdgeCustomization | undefined {
        return this._model;
    }

    public setModel(model: IEdgeCustomization): void {
        this._model = model;
        this.discardChanges();
    }

    /**
     * After multipart font upload: refresh server font rows and filenames without discarding pending catalog-font edits.
     */
    public applyServerPayloadAfterUpload(model: IEdgeCustomization): void {
        runInAction(() => {
            this._model = model;
            Object.values(EDGE_FONT_SLOTS).forEach((slot) => {
                delete this._pendingBySlot[slot];
            });
        });
    }

    public get font(): string | undefined {
        return this.bodyFont ?? this._model?.font;
    }

    public getFamilyForSlot(slot: EdgeFontSlot): string | undefined {
        const patch = this._slotPatch[slot];
        const base = this.rowFromModel(slot);
        return patch?.family ?? base?.family ?? this._model?.font;
    }

    public get headerFont(): string | undefined {
        return this.getFamilyForSlot('header');
    }

    public get bodyFont(): string | undefined {
        return this.getFamilyForSlot('body');
    }

    public useCustomFontForSlot(slot: EdgeFontSlot): boolean {
        if (this._slotPatch[slot]?._delete_custom_font) {
            return false;
        }
        const filesLen = this.rowFromModel(slot)?.custom_font_files?.length ?? 0;
        return filesLen > 0 || this._pendingBySlot[slot] !== undefined;
    }

    public get headerUseCustomFont(): boolean {
        return this.useCustomFontForSlot(EDGE_FONT_SLOTS.HEADER);
    }

    public get bodyUseCustomFont(): boolean {
        return this.useCustomFontForSlot(EDGE_FONT_SLOTS.BODY);
    }

    public customFontFilesForSlot(slot: EdgeFontSlot): IEdgeCustomizationFontFile[] {
        if (this._slotPatch[slot]?._delete_custom_font) {
            return [];
        }
        return this.rowFromModel(slot)?.custom_font_files ?? [];
    }

    /** RMS/API slot field: basename of the last package upload for this slot (when server persisted it). */
    public customFontUploadOriginalNameForSlot(slot: EdgeFontSlot): string | undefined {
        if (this._slotPatch[slot]?._delete_custom_font) {
            return undefined;
        }
        const raw = this.rowFromModel(slot)?.custom_font_upload_original_name;
        if (typeof raw !== 'string') {
            return undefined;
        }
        const t = raw.trim();
        return t !== '' ? t : undefined;
    }

    public get headerCustomFontFiles(): IEdgeCustomizationFontFile[] {
        return this.customFontFilesForSlot(EDGE_FONT_SLOTS.HEADER);
    }

    public get bodyCustomFontFiles(): IEdgeCustomizationFontFile[] {
        return this.customFontFilesForSlot(EDGE_FONT_SLOTS.BODY);
    }

    public hasPendingForSlot(slot: EdgeFontSlot): boolean {
        return this._pendingBySlot[slot] !== undefined;
    }

    public get hasPendingHeaderFontPackage(): boolean {
        return this.hasPendingForSlot(EDGE_FONT_SLOTS.HEADER);
    }

    public get hasPendingBodyFontPackage(): boolean {
        return this.hasPendingForSlot(EDGE_FONT_SLOTS.BODY);
    }

    public pendingDisplayNameForSlot(slot: EdgeFontSlot): string | undefined {
        return this._pendingBySlot[slot]?.name;
    }

    public get pendingHeaderFontDisplayName(): string | undefined {
        return this.pendingDisplayNameForSlot(EDGE_FONT_SLOTS.HEADER);
    }

    public get pendingBodyFontDisplayName(): string | undefined {
        return this.pendingDisplayNameForSlot(EDGE_FONT_SLOTS.BODY);
    }

    public hasPendingFileUploads(): boolean {
        return Object.values(EDGE_FONT_SLOTS).some((slot) => this._pendingBySlot[slot] !== undefined);
    }

    public setFontForSlot(slot: EdgeFontSlot, v: string): void {
        this._slotPatch[slot] = { ...this._slotPatch[slot], family: v };
        this._clearUnneededSlotPatches();
    }

    public setHeaderFont(v: string): void {
        this.setFontForSlot(EDGE_FONT_SLOTS.HEADER, v);
    }

    public setBodyFont(v: string): void {
        this.setFontForSlot(EDGE_FONT_SLOTS.BODY, v);
    }

    public uploadFontPackageForSlot(slot: EdgeFontSlot, file: File): void {
        this._pendingBySlot[slot] = file;
        if (this._slotPatch[slot]) {
            const next = { ...this._slotPatch[slot] };
            delete next._delete_custom_font;
            if (Object.keys(next).length === 0) {
                delete this._slotPatch[slot];
            } else {
                this._slotPatch[slot] = next;
            }
        }
    }

    public uploadHeaderFontPackage(file: File): void {
        this.uploadFontPackageForSlot(EDGE_FONT_SLOTS.HEADER, file);
    }

    public uploadBodyFontPackage(file: File): void {
        this.uploadFontPackageForSlot(EDGE_FONT_SLOTS.BODY, file);
    }

    public deleteCustomFontForSlot(slot: EdgeFontSlot): void {
        const hasServerOrPending =
            (this.rowFromModel(slot)?.custom_font_files?.length ?? 0) > 0 || this.hasPendingForSlot(slot);
        this._slotPatch[slot] = {
            ...this._slotPatch[slot],
            _delete_custom_font: hasServerOrPending,
        };
        delete this._pendingBySlot[slot];
    }

    public deleteHeaderCustomFont(): void {
        this.deleteCustomFontForSlot(EDGE_FONT_SLOTS.HEADER);
    }

    public deleteBodyCustomFont(): void {
        this.deleteCustomFontForSlot(EDGE_FONT_SLOTS.BODY);
    }

    public hasChanges(): boolean {
        return (
            Object.keys(this._slotPatch).length > 0 ||
            Object.values(EDGE_FONT_SLOTS).some((slot) => this._pendingBySlot[slot] !== undefined)
        );
    }

    public hasScalarOrDeleteChangesOnly(): boolean {
        return Object.keys(this._slotPatch).length > 0;
    }

    public discardChanges(): void {
        this._slotPatch = {};
        Object.values(EDGE_FONT_SLOTS).forEach((slot) => {
            delete this._pendingBySlot[slot];
        });
    }

    private buildFontSlotsForOverlay(): FontSlotPutEntry[] {
        const model = this._model;
        if (!model) {
            return [];
        }
        return Object.values(EDGE_FONT_SLOTS).map((slot) => {
            const patch = this._slotPatch[slot];
            const base = this.rowFromModel(slot);
            const family = (patch?.family ?? base?.family ?? model.font) as string;
            const custom_font_files = this.customFontFilesForSlot(slot);
            const entry: FontSlotPutEntry = {
                slot,
                family,
                custom_font_files,
            };
            const uploadName = this.customFontUploadOriginalNameForSlot(slot);
            if (uploadName !== undefined) {
                entry.custom_font_upload_original_name = uploadName;
            }
            if (patch && Object.prototype.hasOwnProperty.call(patch, '_delete_custom_font')) {
                entry._delete_custom_font = patch._delete_custom_font;
            }
            return entry;
        });
    }

    public getUpdatedModel(): IEdgeCustomization | undefined {
        if (!this._model) {
            return undefined;
        }
        return {
            ...this._model,
            font: (this.font ?? this.bodyFont ?? this.headerFont ?? this._model.font) as string,
            font_slots: this.buildFontSlotsForOverlay(),
        };
    }

    /**
     * Font-related keys merged onto the main Edge PUT body (resolved getters + delete flags when dirty).
     */
    public getFontOverlayForPut(): Pick<IEdgeCustomization, 'font' | 'font_slots'> {
        return {
            font: (this.font ?? this.bodyFont ?? this.headerFont ?? this._model?.font) as string,
            font_slots: this.buildFontSlotsForOverlay(),
        };
    }

    /**
     * POST pending multipart uploads only (no PUT). Updates local model from the last response payload.
     */
    public async uploadPendingFontFiles(): Promise<IEdgeCustomization | undefined> {
        if (!this._model) {
            return undefined;
        }
        if (!this.hasPendingFileUploads()) {
            return undefined;
        }

        this._isSaving = true;
        let latest: IEdgeCustomization | undefined;
        try {
            for (const slot of Object.values(EDGE_FONT_SLOTS)) {
                const file = this._pendingBySlot[slot];
                if (file) {
                    latest = await this.service.uploadEdgeFontPackage(slot, file);
                }
            }
            if (latest) {
                const payload = latest;
                runInAction(() => this.applyServerPayloadAfterUpload(payload));
            }
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
        return latest;
    }

    public async save(): Promise<void> {
        await this.uploadPendingFontFiles();
    }

    private _clearUnneededSlotPatches(): void {
        Object.values(EDGE_FONT_SLOTS).forEach((slot) => {
            const cur = this._slotPatch[slot];
            if (!cur) {
                return;
            }
            const base = this.rowFromModel(slot);
            const next: ISlotDraft = { ...cur };
            if (next.family !== undefined && next.family === base?.family) {
                delete next.family;
            }
            if (Object.keys(next).length === 0) {
                delete this._slotPatch[slot];
            } else {
                this._slotPatch[slot] = next;
            }
        });
    }
}

export default CustomizationEdgeFontsStore;
